import axios from 'axios'
import type { Edge } from '@xyflow/react'
import apiClient from './client'
import type { FloorPlanFreehandStyle } from '../components/floorPlanFreehand'

export interface FloorPlanImage {
  id: number
  schema_id: number
  original_filename: string
  mime_type: string
  file_size: number
  image_width: number
  image_height: number
  created_at: string
  updated_at: string
}

export interface FloorPlanItem {
  id: number
  schema_id: number
  device_instance_id: number
  entity_type?: string
  image_x: number
  image_y: number
  label_dx: number
  label_dy: number
  marker_size: number
  visible: number
  layer_id?: number | string | null
  properties?: string | null
  position_label?: string
  description?: string
  knx_address?: string
  is_reserve?: number
  specification_id?: number
  spec_name?: string
  manufacturer?: string
  model?: string
  device_type_code?: string
  device_type_name?: string
}

export type FloorPlanShapeType =
  | 'circle'
  | 'round-rectangle'
  | 'rectangle'
  | 'square'
  | 'hexagon'
  | 'diamond'
  | 'document'
  | 'cylinder'
  | 'triangle'
  | 'parallelogram'
  | 'plus'
  | 'ellipse'

export interface FloorPlanShapeStyle {
  kind: 'shape'
  shapeType: FloorPlanShapeType
  label: string
  width: number
  height: number
  fill: string
  stroke: string
  strokeWidth: number
}

export type FloorPlanNodeProperties = FloorPlanShapeStyle | FloorPlanFreehandStyle

export interface FloorPlanShape {
  id: number
  schema_id: number
  entity_id: number
  entity_type: 'infrastructure'
  image_x: number
  image_y: number
  layer_id?: number | string | null
  properties: FloorPlanNodeProperties
}

/** Слой схемы «На плане» */
export interface FloorPlanLayer {
  id: number
  schema_id: number
  name: string
  sort_order: number
  visible: number
  locked: number
  svg_markup: string | null
  svg_color: string | null
  svg_stroke_width: number | string
  marker_scale: number | string
  label_font_size: number | string
  label_bg_color: string | null
  line_algorithm: string | null
  line_color: string | null
  line_stroke_width: number | string
  line_stroke_dasharray: string | null
  created_at?: string
  updated_at?: string
}

export interface FloorPlanPayload {
  image: FloorPlanImage | null
  items: FloorPlanItem[]
  shapes: FloorPlanShape[]
  edges: Edge[]
  layers: FloorPlanLayer[]
  schema?: {
    id: number
    name: string
  } | null
  electrical_panel?: {
    id: number
    name: string
  } | null
}

/** Убирает двойную обёртку, если interceptor не сработал */
function normalizeFloorPlanPayload(raw: unknown): FloorPlanPayload {
  if (!raw || typeof raw !== 'object') {
    return { image: null, items: [], shapes: [], edges: [], layers: [], schema: null, electrical_panel: null }
  }
  const r = raw as Record<string, unknown>
  if ('data' in r && r.data && typeof r.data === 'object') {
    const inner = r.data as Record<string, unknown>
    if ('image' in inner || 'items' in inner) {
      return {
        image: (inner.image as FloorPlanImage | null) ?? null,
        items: Array.isArray(inner.items) ? (inner.items as FloorPlanItem[]) : [],
        shapes: Array.isArray(inner.shapes) ? (inner.shapes as FloorPlanShape[]) : [],
        edges: Array.isArray(inner.edges) ? (inner.edges as Edge[]) : [],
        layers: Array.isArray(inner.layers) ? (inner.layers as FloorPlanLayer[]) : [],
        schema: (inner.schema as FloorPlanPayload['schema']) ?? null,
        electrical_panel: (inner.electrical_panel as FloorPlanPayload['electrical_panel']) ?? null,
      }
    }
  }
  if ('image' in r || 'items' in r) {
    return {
      image: (r.image as FloorPlanImage | null) ?? null,
      items: Array.isArray(r.items) ? (r.items as FloorPlanItem[]) : [],
      shapes: Array.isArray(r.shapes) ? (r.shapes as FloorPlanShape[]) : [],
      edges: Array.isArray(r.edges) ? (r.edges as Edge[]) : [],
      layers: Array.isArray(r.layers) ? (r.layers as FloorPlanLayer[]) : [],
      schema: (r.schema as FloorPlanPayload['schema']) ?? null,
      electrical_panel: (r.electrical_panel as FloorPlanPayload['electrical_panel']) ?? null,
    }
  }
  return { image: null, items: [], shapes: [], edges: [], layers: [], schema: null, electrical_panel: null }
}

export const getFloorPlan = async (schemaId: number): Promise<FloorPlanPayload> => {
  const response = await apiClient.get(`/api/floor_plan?schema_id=${schemaId}`)
  return normalizeFloorPlanPayload(response.data)
}

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]

function isPngMagic(buf: ArrayBuffer): boolean {
  if (buf.byteLength < 8) return false
  const u = new Uint8Array(buf)
  return PNG_MAGIC.every((b, i) => u[i] === b)
}

/** Загрузка PNG тем же клиентом, что и JSON API (сессия как у axios), без «голого» fetch/img */
export const fetchFloorPlanImageBlob = async (schemaId: number, cacheBust: string): Promise<Blob> => {
  try {
    const response = await apiClient.get(`/api/floor_plan/image/${schemaId}`, {
      responseType: 'blob',
      params: { v: cacheBust },
    })
    const blob = response.data as Blob
    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error('Пустой ответ при загрузке изображения')
    }
    const head = await blob.slice(0, 8).arrayBuffer()
    const jsonProbe = new TextDecoder('utf-8', { fatal: false }).decode(new Uint8Array(head)).trimStart()
    if (jsonProbe.startsWith('{') || jsonProbe.startsWith('[')) {
      const text = await blob.text()
      let msg = 'Сервер вернул не изображение'
      try {
        const j = JSON.parse(text) as { error?: string }
        if (typeof j.error === 'string') msg = j.error
      } catch {
        /* ignore */
      }
      throw new Error(msg)
    }
    if (!isPngMagic(head)) {
      throw new Error('Ответ не похож на PNG')
    }
    return blob
  } catch (e) {
    if (axios.isAxiosError(e)) {
      const st = e.response?.status
      if (st === 401) throw new Error('Нет доступа к файлу плана (сессия)')
      if (st === 404) throw new Error('Файл плана не найден')
      if (typeof st === 'number') throw new Error(`Ошибка загрузки изображения (${st})`)
    }
    throw e
  }
}

export const uploadFloorPlanImage = async (schemaId: number, file: File): Promise<any> => {
  const formData = new FormData()
  formData.append('schema_id', String(schemaId))
  // Явное имя файла — часть окружений без него даёт пустой upload на сервере
  formData.append('file', file, file.name || 'plan.png')

  // multipart через fetch: браузер сам ставит boundary; axios с дефолтным JSON-заголовком
  // иногда ломает тело запроса так, что PHP не видит $_FILES['file'].
  const url = `/api/floor_plan/image/${schemaId}`
  const res = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  })

  const text = await res.text()
  let json: { success?: boolean; data?: unknown; error?: string }
  try {
    json = JSON.parse(text) as typeof json
  } catch {
    throw new Error(text || `HTTP ${res.status}`)
  }

  if (!res.ok || json.success === false) {
    throw new Error(typeof json.error === 'string' ? json.error : `HTTP ${res.status}`)
  }

  return json.data !== undefined ? json.data : json
}

export const deleteFloorPlanImage = async (schemaId: number): Promise<void> => {
  await apiClient.delete('/api/floor_plan/image', {
    data: { schema_id: schemaId },
  })
}

export const addFloorPlanItem = async (payload: {
  schema_id: number
  device_instance_id?: number
  entity_id?: number
  entity_type?: 'device' | 'cable' | 'infrastructure'
  image_x: number
  image_y: number
  label_dx?: number
  label_dy?: number
  marker_size?: number
  visible?: boolean
  layer_id?: number
}): Promise<{ id: number }> => {
  const response = await apiClient.post('/api/floor_plan/items', payload)
  return response.data as { id: number }
}

export const addFloorPlanShape = async (payload: {
  schema_id: number
  image_x: number
  image_y: number
  layer_id?: number
  properties: FloorPlanNodeProperties
}): Promise<{ id: number }> => {
  const response = await apiClient.post('/api/floor_plan/items', {
    ...payload,
    entity_type: 'infrastructure',
  })
  return response.data as { id: number }
}

export const updateFloorPlanItem = async (
  itemId: number,
  payload: Partial<{
    image_x: number
    image_y: number
    label_dx: number
    label_dy: number
    marker_size: number
    visible: boolean
    layer_id: number | null
    properties: FloorPlanNodeProperties | Record<string, unknown> | null
  }>
): Promise<void> => {
  await apiClient.put(`/api/floor_plan/items/${itemId}`, payload)
}

export const getFloorPlanLayers = async (schemaId: number): Promise<FloorPlanLayer[]> => {
  const response = await apiClient.get(`/api/floor_plan/layers?schema_id=${schemaId}`)
  return Array.isArray(response.data) ? (response.data as FloorPlanLayer[]) : []
}

export const createFloorPlanLayer = async (
  payload: Partial<FloorPlanLayer> & { schema_id: number; name?: string }
): Promise<FloorPlanLayer> => {
  const response = await apiClient.post('/api/floor_plan/layers', payload)
  return response.data as FloorPlanLayer
}

export const updateFloorPlanLayer = async (
  layerId: number,
  payload: Partial<Omit<FloorPlanLayer, 'id' | 'schema_id'>>
): Promise<FloorPlanLayer> => {
  const response = await apiClient.put(`/api/floor_plan/layers/${layerId}`, payload)
  return response.data as FloorPlanLayer
}

export const deleteFloorPlanLayer = async (
  layerId: number,
  targetLayerId?: number
): Promise<{ reassigned_to_layer_id?: number }> => {
  const response = await apiClient.delete(`/api/floor_plan/layers/${layerId}`, {
    data: targetLayerId ? { target_layer_id: targetLayerId } : {},
  })
  return (response.data as { reassigned_to_layer_id?: number }) ?? {}
}

export const removeFloorPlanItem = async (itemId: number): Promise<void> => {
  await apiClient.delete(`/api/floor_plan/items/${itemId}`)
}

export const updateFloorPlanEdges = async (schemaId: number, edges: Edge[]): Promise<void> => {
  await apiClient.put('/api/floor_plan/edges', {
    schema_id: schemaId,
    edges,
  })
}

export const getFloorPlanImageUrl = (schemaId: number): string => {
  return `/api/floor_plan/image/${schemaId}`
}
